import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import dynamic from 'next/dynamic';
import { connect } from 'react-redux';
import { formatMoney } from 'accounting-js';
import { toast } from 'react-toastify';

import { trackAddToCart, trackLineItemChange, LUX } from '../../utils/tracking';
import { updateCart } from '../../store/modules/cart/actions';
import { addToCart } from '../../pages/api';

import getCookie from '../../utils/getCookie';
import asModal from '../../utils/asModal';

import Button from '../../atoms/button';
import Monogram from '../product-details-monogrammable';
import RegistryButton from '../../molecules/registry-button';
import Select from '../../atoms/select';
import Typography from '../../atoms/typography';
import WaitlistEmail from '../../molecules/product-waitlist-email';
import Checkbox from '../../atoms/checkbox';
import AddToWishlist from '../../molecules/add-to-wishlist';
import IconShirt from '../../atoms/icon-tshirt';

const FormWrapper = styled.form``;

const LazySizeGuide = dynamic(() => import('../../organs/size-guide'), { ssr: false });
const SizeGuideModal = asModal(LazySizeGuide);

const QuantitySelect = styled(Select)`
  outline: 0;
  margin-bottom: 2rem;
  color: ${(props) => props.theme.color.brand};
`;

const QuantitySelectLabel = styled.label`
  opacity: 0;
  pointer-events: none;
  position: absolute;
`;

const SubmitButton = styled(Button)`
  margin-bottom: 1rem;
  outline: 0;
  padding: 0 1rem;
  width: 100%;

  @media (min-width: ${(props) => props.theme.breakpoint.large}) {
    margin-bottom: 0;
  }
`;

const Option = styled.input`
  opacity: 0;
  position: absolute;
  z-index: -1;
`;

const OptionLabel = styled(Typography)`
  align-items: center;
  border: 1px solid ${(props) => props.theme.color.brand};
  color: ${(props) => props.theme.color.brand};
  cursor: pointer;
  display: flex;
  flex-direction: row;
  height: 3rem;
  justify-content: center;
  line-height: 3rem;
  margin: 1rem 1rem 0 0;
  min-width: 6rem;
  padding: 0 1rem;
  position: relative;
  text-align: center;
  transition: opacity ${(props) => props.theme.animation.default} ${(props) => props.theme.animation.easeOutQuad};

  :hover {
    opacity: 0.75;
  }

  ${(props) => props.active && css`
    background-color: ${props.theme.color.brand};
    color: ${props.theme.color.white};
  `};

  ${(props) => (
    props.disabled
      ? css`
        position: relative;
        cursor: not-allowed;
        opacity: .5;

        ::before {
          content: "";
          position: absolute;
          left: 0;
          top: 0;
          right: 0;
          bottom: 0;
          width: 100%;
          background: linear-gradient(to top left, transparent calc(50% - 1px), ${props.theme.color.brand}, transparent calc(50% + 1px));
          z-index: -1;
        }
      ` : css`
        :hover {
          opacity: 0.75;
        }
      `
  )}
`;

const OptionGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 1rem;
  margin-top: -1rem;
`;

const OptionHelper = styled(Typography)`
  color: ${(props) => props.theme.color.brand};
  letter-spacing: 0.2em;
  line-height: 2rem;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
`;

const OptionText = styled.span`
  color: ${(props) => props.theme.color.brandLight};
`;

const OptionInvalid = styled.span`
  color: ${(props) => props.theme.color.brandA11yRed};
`;

const OpenSizeGuideButton = styled(Button)`
  align-items: center;
  display: flex;
  flex-direction: row;
  font-family: ${(props) => props.theme.font.sans};
  margin-bottom: 2rem;

  > svg {
    width: 2rem;
    fill: ${(props) => props.theme.color.brand};
    margin-right: 0.5rem;
  }
`;

const AddToCartWrapper = styled.div`
  margin-bottom: 3rem;

  ${({ isQuickshop }) => isQuickshop && css`
    margin-bottom: 0;
  `}
`;

const ButtonWrapper = styled.span`
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;

  ${(props) => props.hasRegistry && css`
      @media (min-width: ${props.theme.breakpoint.large}) {
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-column-gap: 2rem;
        grid-row-gap: 0.5rem;
      }
  `}
`;

const Monogrammable = styled(Monogram)`
  margin-bottom: 2rem;
`;

const MonogramCheckbox = styled(Checkbox)`
  margin-bottom: 2rem;
`;

const TextFlair = styled(Typography)`
  align-self: center;
  color: ${({ theme }) => theme.color.brandA11yRed};
  letter-spacing: 0.1em;
  position: relative;
  text-align: center;
  text-transform: uppercase;
  padding: 0 ${({ theme }) => theme.modularScale.base};
`;

const ProductAdd = (props) => {
  const getOptionType = () => props.product?.option_types?.[0]?.name || 'Size';

  const [addingToCart, setAddingToCart] = useState(false);
  const [addMonogram, setAddMonogram] = useState(false);
  const [buttonText, setButtonText] = useState('Add to Bag');
  const [isValid, setIsValid] = useState(false);
  const [monogramColor, setMonogramColor] = useState(false);
  const [monogramFont, setMonogramFont] = useState(false);
  const [monogramText, setMonogramText] = useState(false);
  const [onlyOneLeft, setOnlyOneLeft] = useState(false);
  const [option, setOption] = useState(null);
  const [optionType] = useState(getOptionType());
  const [outOfStock, setOutOfStock] = useState(false);
  const [quantity, setQuantity] = useState('1');
  const [quantityChanged, setQuantityChanged] = useState(false);
  const [sizeGuideActive, setSizeGuideActive] = useState(false);
  const [variant, setVariant] = useState(null);

  useEffect(() => {
    if (props.product?.variants?.length === 1) {
      const [v] = props.product.variants;
      const o = v.option_values[0].name;

      props.onVariantChange(v);

      setAddMonogram(v?.prices?.[0]?.monogram?.monogrammable_only ?? false);
      setOnlyOneLeft(v.total_on_hand === 1);
      setOption(o);
      setOutOfStock(v.total_on_hand === 0);
      setQuantity('1');
      setQuantityChanged(false);
      setVariant(v);
    }

    if (props.product?.variants?.every((v) => v.in_stock === false)) {
      setButtonText('Out of Stock');
    }
  }, []);

  const getOptions = () => {
    const { variants } = props.product;

    const options = variants.filter((v) => v.option_values.length > 0).map(
      (v) => ({
        label: v.option_values[0].name,
        position: v.option_values[0].position,
        count: v.total_on_hand,
        variant: v
      })
    );

    return options.sort((a, b) => ((a.position < b.position) ? -1 : 1));
  };

  const getCalculatedPrice = () => {
    if (variant && quantity > 0) {
      const vendor = variant.prices.find((x) => x.vendor_id === props.vendor);

      const vendorPrice = vendor.price;
      const monogramPrice = +variant.prices[0]?.monogram?.monogram_price ?? 0;

      const variantTotal = vendorPrice * quantity;
      const monogramTotal = addMonogram ? monogramPrice * quantity : 0;
      const total = variantTotal + monogramTotal;

      const price = formatMoney(total, { precision: 2 });
      return ` - ${price}`;
    }

    return null;
  };

  const handleAddToCart = async ({
    count,
    userId,
    variantId,
    vendorId
  }) => {
    const { monogram_customizations } = variant.prices[0].monogram;

    if (props.product.variants.length > 1) {
      setButtonText('Adding to Bag...');
      setQuantity('1');
      setOption(null);
      setVariant(null);
      setAddingToCart(true);
      props.onVariantChange(null);
    } else {
      setButtonText('Adding to Bag...');
      setQuantity('1');
      setAddingToCart(true);
    }

    const userToken = getCookie('maisonette_user_token');
    const orderToken = getCookie('maisonette_order_token');

    const request = {
      line_item: {
        quantity: count,
        variant_id: variantId,
        vendor_id: vendorId
      }
    };

    if (addMonogram) {
      request.line_item.monogram_attributes = {};
      request.line_item.monogram_attributes.text = monogramText;

      if (monogram_customizations?.colors?.length > 0) {
        request
          .line_item
          .monogram_attributes
          .customization = request.line_item.monogram_attributes.customization || {};

        request.line_item.monogram_attributes.customization.color = monogram_customizations.colors
          .find((color) => color.value === monogramColor.hex);
      }

      if (monogram_customizations?.fonts?.length > 0) {
        request
          .line_item
          .monogram_attributes
          .customization = request.line_item.monogram_attributes.customization || {};

        request.line_item.monogram_attributes.customization.font = monogram_customizations.fonts
          .find((font) => font.value === monogramFont);
      }
    }

    if (userToken && userId) request.user_id = userId;
    if (orderToken !== undefined && orderToken !== 'undefined') request.order_token = orderToken;

    LUX.addedToCart();

    const abortController = new global.window.AbortController();

    await addToCart({ body: request, signal: abortController.signal })
      .then((res) => {
        // if the user adds an item to the cart and quickly tries to navigate away
        // the request will abort and continue with the .then block.
        // aborting the request will prevent this from happening
        if (Object.keys(res).length === 0) abortController.abort();
        if (abortController.signal.aborted) return;

        if (res?.errors && Array.isArray(res.errors)) {
          res.errors.forEach(({ message = null }) => {
            if (typeof message === 'string') {
              toast(message, { type: toast.TYPE.ERROR });
            }
          });

          setButtonText('Add to Bag');
          setAddingToCart(false);
          return;
        }

        props.updateCart(res);

        global.document.cookie = `maisonette_order_token=${res.token}; path=/;`;
        global.document.cookie = `maisonette_order_number=${res.number}; path=/;`;

        setButtonText('Added to Bag ✓');

        LUX.cartValue(res);
        LUX.cartSize(res);

        const trackVariant = props?.product?.variants?.find((v) => v.id === variantId);

        trackAddToCart({
          product: props.product,
          cart: res,
          quantity: count,
          variant: trackVariant,
          user: props.user
        });

        setTimeout(() => {
          setButtonText('Add to Bag');
          setAddingToCart(false);
        }, 1500);
      });
  };

  const canSubmit = () => {
    if (props.cart.loading) return false;

    let isMonogramValid = true;

    if (variant?.prices?.length && addMonogram) {
      const { monogram_customizations = {} } = variant.prices[0].monogram;

      let isColorValid = true;
      let isFontValid = true;

      if (Object.prototype.hasOwnProperty.call(monogram_customizations, 'colors')) {
        isColorValid = monogram_customizations.colors.length === 0 || monogramColor;
      }

      if (Object.prototype.hasOwnProperty.call(monogram_customizations, 'fonts')) {
        isFontValid = monogram_customizations.fonts.length === 0 || monogramFont;
      }

      const isTextValid = monogramText;

      isMonogramValid = !!isColorValid && !!isFontValid && !!isTextValid;
    }

    return isMonogramValid && quantity !== '0' && option;
  };

  const handleOnMonogramClick = () => {
    setAddMonogram(!addMonogram);

    if (!addMonogram) {
      setMonogramColor(false);
      setMonogramFont(false);
      setMonogramText(false);
    }
  };

  const handleMonogramOnly = () => {
    setAddMonogram(true);
    setMonogramColor(false);
    setMonogramFont(false);
    setMonogramText(false);
  };

  const handleOnOptionSelect = (e, v) => {
    const { value } = e.currentTarget;
    props.onVariantChange(v);

    const monogram = v?.prices[0]?.monogram;
    if (monogram?.monogrammable_only) handleMonogramOnly();

    setOption(value);
    setOnlyOneLeft(v.total_on_hand === 1);
    setOutOfStock(v.total_on_hand === 0);
    setQuantity('1');
    setQuantityChanged(false);
    setVariant(v);
  };

  const handleOnSelectChange = (e) => {
    setQuantity(e.currentTarget.value);
    setQuantityChanged(e.currentTarget.value > 0);

    trackLineItemChange({
      product: props.product,
      quantity: e.currentTarget.value,
      variant
    });
  };

  const hasSizeGuideProperty = () => !!props?.product?.product_properties?.find((x) => x.property_name === 'Size Guide');

  const reachedStockLimit = () => {
    if (variant && variant.total_on_hand > 0) {
      const itemQuantity = props.cart?.line_items?.
        find((x) => x.variant_id === variant?.id)?.quantity || 0;

      const priceObj = variant.prices.find((x) => x.vendor_id === props.vendor);

      const max = 10;

      const count = priceObj.total_on_hand - itemQuantity > max
        ? max : priceObj.total_on_hand - itemQuantity;

      return count <= 0;
    }

    return false;
  };

  const renderQuantityOptions = () => {
    if (variant && variant.total_on_hand > 0) {
      const itemQuantity = props.cart?.line_items?.
        find((x) => x.variant_id === variant?.id)?.quantity || 0;

      const priceObj = variant.prices.find((x) => x.vendor_id === props.vendor);

      const max = 10;

      const count = priceObj.total_on_hand - itemQuantity > max
        ? max : priceObj.total_on_hand - itemQuantity;

      const options = [];
      for (let i = 1; i <= count; i++) {
        options.push(<option key={i}>{i}</option>);
      }

      return options;
    }

    return [];
  };

  const isProductAvailable = () => props.product.available;
  const isProductDiscontinued = () => props.product.discontinued;
  const isVariantDiscontinued = () => variant && variant.discontinued;

  useEffect(() => {
    setVariant(props.variant);
    setMonogramColor(false);
    setMonogramFont(false);
    setMonogramText(false);
    setIsValid(canSubmit());
  }, [props.variant]);

  useEffect(() => {
    setMonogramColor(false);
    setMonogramFont(false);
    setMonogramText(false);
  }, [addMonogram]);

  useEffect(() => {
    setIsValid(canSubmit());
  }, [addMonogram, monogramColor, monogramFont, monogramText]);

  useEffect(() => {
    setIsValid(canSubmit());
  }, [props.cart.loading]);

  const availabilityMessage = isProductDiscontinued()
    ? 'This product is no longer available'
    : 'This product is not available yet';

  const handleOnSubmit = (e) => {
    e.preventDefault();

    handleAddToCart({
      variantId: props.variant.id,
      vendorId: props.vendor,
      count: quantity,
      userId: props.userId
    });
  };

  return (
    <AddToCartWrapper isQuickshop={props.isQuickshop} onSubmit={handleOnSubmit}>
      {
        isProductDiscontinued() || !isProductAvailable()
          ? (
            <TextFlair element="p" like="label-1">
              {availabilityMessage}
            </TextFlair>
          ) : (
            <FormWrapper id="add-to-cart-form">
              {(props.product?.variants?.length > 1 && option !== 'OS') && (
                <>
                  <OptionHelper element="p" like="label-1">
                    {optionType}
                    {': '}
                    {
                      option
                        ? (
                          <>
                            {option}
                            {outOfStock && <OptionInvalid> Out of Stock</OptionInvalid>}
                            {
                              onlyOneLeft && (
                                <OptionInvalid>
                                  {reachedStockLimit() ? ' No More Left' : ' Only 1 Left'}
                                </OptionInvalid>
                              )
                            }
                          </>
                        ) : <OptionText>{`Select a ${optionType}`}</OptionText>
                    }
                  </OptionHelper>

                  <OptionGroup>
                    {
                      getOptions().map((o) => (
                        <OptionLabel
                          tabIndex="0"
                          active={option === o.label ? 'true' : undefined}
                          element="label"
                          like="dec-1"
                          key={o.label}
                          disabled={o.count <= 0}
                          htmlFor={`product-option-${o.label}`}
                        >
                          <Option
                            id={`product-option-${o.label}`}
                            name="product-option"
                            type="radio"
                            value={o.label}
                            checked={option === o.label}
                            onChange={(e) => handleOnOptionSelect(e, o.variant)}
                          />
                          {o.label}
                        </OptionLabel>
                      ))
                    }
                  </OptionGroup>

                  {
                    hasSizeGuideProperty() && (
                      <OpenSizeGuideButton
                        styledLikeLink
                        onClick={() => setSizeGuideActive(true)}
                      >
                        <IconShirt />
                        Size Guide
                      </OpenSizeGuideButton>
                    )
                  }

                  <SizeGuideModal
                    active={sizeGuideActive}
                    onClose={() => setSizeGuideActive(false)}
                  />
                </>
              )}

              <QuantitySelectLabel htmlFor="product-add-to-cart-quantity-select">Add to Bag Quantity Select</QuantitySelectLabel>

              {
                isVariantDiscontinued()
                  ? (
                    <TextFlair element="p" like="label-1">
                      The selected Size is not available
                    </TextFlair>
                  ) : (
                    <>
                      <QuantitySelect
                        name="quantity"
                        id="product-add-to-cart-quantity-select"
                        disabled={outOfStock || !option || reachedStockLimit()}
                        value={outOfStock ? '0' : quantity}
                        changed={quantityChanged}
                        onChange={handleOnSelectChange}
                        outline
                      >
                        <option value="0">-- Select a Quantity --</option>
                        {renderQuantityOptions()}
                      </QuantitySelect>
                      {
                        variant?.in_stock
                        && variant?.prices[0]?.monogram?.monogrammable && (
                          <>
                            {
                              variant.prices[0].monogram.monogrammable_only
                                ? (
                                  <Monogrammable
                                    variant={props.variant.id}
                                    colors={
                                      variant.prices[0].monogram.monogram_customizations.colors
                                    }
                                    fonts={
                                      variant.prices[0].monogram.monogram_customizations.fonts
                                    }
                                    maxTextLength={
                                      variant.prices[0].monogram.monogram_max_text_length
                                    }
                                    setColor={(color) => setMonogramColor(color)}
                                    setFont={(font) => setMonogramFont(font)}
                                    setText={(text) => setMonogramText(text)}
                                  />
                                ) : (
                                  <>
                                    <MonogramCheckbox
                                      id="pdp-monogram"
                                      name="pdp-monogram"
                                      value={addMonogram}
                                      active={addMonogram}
                                      changed={handleOnMonogramClick}
                                    >
                                      {`Add monogram +${formatMoney(variant.prices[0].monogram.monogram_price)}`}
                                    </MonogramCheckbox>
                                    {addMonogram && (
                                      <Monogrammable
                                        variant={props.variant.id}
                                        colors={
                                          variant.prices[0]
                                            .monogram.monogram_customizations.colors
                                        }
                                        fonts={
                                          variant.prices[0]
                                            .monogram.monogram_customizations.fonts
                                        }
                                        maxTextLength={
                                          variant.prices[0].monogram.monogram_max_text_length
                                        }
                                        setColor={(color) => setMonogramColor(color)}
                                        setFont={(font) => setMonogramFont(font)}
                                        setText={(text) => setMonogramText(text)}
                                      />
                                    )}
                                  </>
                                )
                            }
                          </>
                        )
                      }

                      <ButtonWrapper
                        hasRegistry={!!props.product?.variants?.find((v) => v.is_registry)}
                      >
                        <SubmitButton
                          type="submit"
                          data-test-id="add_to_cart"
                          disabled={
                            reachedStockLimit()
                            || outOfStock
                            || !isValid
                          }
                        >
                          {
                            outOfStock
                              ? 'Out of Stock'
                              : (
                                <>
                                  {reachedStockLimit() ? 'No More Left' : buttonText}
                                  {
                                    !addingToCart
                                    && !reachedStockLimit()
                                    && getCalculatedPrice()
                                  }
                                </>
                              )
                          }
                        </SubmitButton>

                        <RegistryButton
                          data-test-id="add_to_registry"
                          canSubmit={!!isValid}
                          variant={props.variant}
                          variants={props.product.variants}
                          quantity={quantity}
                        />
                      </ButtonWrapper>
                      <AddToWishlist product={props.product} />
                    </>
                  )
              }
            </FormWrapper>
          )
      }

      <WaitlistEmail active={outOfStock} variant={variant?.id ?? null} />
    </AddToCartWrapper>
  );
};

ProductAdd.defaultProps = {
  cart: {},
  isQuickshop: false,
  updateCart: () => { },
  user: null,
  userId: null,
  variant: null,
  vendor: null
};

ProductAdd.propTypes = {
  cart: PropTypes.object,
  isQuickshop: PropTypes.bool,
  onVariantChange: PropTypes.func.isRequired,
  product: PropTypes.object.isRequired,
  updateCart: PropTypes.func,
  user: PropTypes.object,
  userId: PropTypes.number,
  variant: PropTypes.object,
  vendor: PropTypes.number
};

const mapStateToProps = (state) => ({
  cart: state.cart,
  lists: state.lists,
  token: state.user.spree_api_key,
  userId: state.profile.id,
  user: state.profile
});

const mapDispatchToProps = (dispatch) => ({
  updateCart: (cart) => dispatch(updateCart(cart))
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductAdd);
